import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { addSubAdminApi, updateAdmin } from "store/actions";
import { useDispatch } from "react-redux";

const permissionsList = [
  "profile",
  "change_password",
  "statistics",
  "manage_users",
  "game_history",
  "leaderboard",
  "claim_history",
  "logs",
  "tasks",
  "roadmap",
  "add_roadmap",
  "edit_roadmap",
  "delete_roadmap",
  "add_task",
  "edit_task",
  "delete_task",
  "edit_leaderboard_points",
  "edit_game_settings",
  "edit_leaderboard_settings",
  "edit_energy_settings",
  "edit_maintenance_settings",
  "edit_users",
  "game_settings",
  "leaderboard_settings",
  "energy_settings",
  "maintenance_settings",
  "welcome_bonus_settings",
  "edit_welcome_bonus_settings",
  "crypto_exchange_settings",
  "edit_crypto_exchange_settings",
  "edit_conversion_settings",
  "conversion_settings",
  "promotion_settings",
  "edit_promotion_settings",
  "crypto_deposit_settings",
  "edit_crypto_deposit_settings",
  "energy_package",
  "add_energy_package",
  "edit_energy_package",
  "delete_energy_package",
  "payment_setting",
  "edit_payment_setting",
  "buy_energy_setting",
  "edit_buy_energy_setting",
  "energy_purchase_history",
  "reward_system_settings",
  "edit_reward_system_settings",
  "jackpot_settings",
  "edit_jackpot_settings",
  "update_user_balance",
  "historical_data_setting",
  "edit_historical_data_setting",
  "move_data_mySql",
  "remove_data_mongoDb",
  "delete_data_from_mongoDb",
  "round_multiplier",
  "add_round_multiplier",
  "edit_round_multiplier",
  "delete_round_multiplier",
  "system_announcement",
  "add_system_announcement",
  "view_system_announcement",
  "export_request",
  "download_export_request",
  "coin_history",
  "coin_list",
  "add_role",
  "edit_role",
  "view_role",
  "delete_role",
  "add_permission",
  "edit_permission",
  "view_permission",
  "delete_permission",
  "add_permission_meta",
  "edit_permission_meta",
  "view_permission_meta",
  "delete_permission_meta",
  "add_admin", 
  "view_admin", 
  "edit_admin", 
  "delete_admin"
];

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  permissions: Yup.array()
    .of(Yup.string())
    .required("At least one permission is required"),
  // status: Yup.string().required('Status is required'),
});

const editvalidationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  permissions: Yup.array()
    .of(Yup.string())
    .required("At least one permission is required"),
  // status: Yup.string().required('Status is required'),
});

export default function AddSubAdmin({
  show,
  handleClose,
  handleConfirm,
  rowData,
}) {
  const [initialValues, setInitialValues] = useState({
    name: "",
    email: "",
    password: "",
    permissions: ["profile"],
    // permissions: [],
    // status: 'active',
  });

  useEffect(() => {
    if (rowData) {
      let perms = rowData?.permissions || [];

      // Always ensure "profile" exists in edit mode
      if (!perms.includes("profile")) {
        perms = ["profile", ...perms];
      }
      setInitialValues({
        name: rowData?.name,
        email: rowData?.email,
        password: rowData?.password,
        permissions: perms,
        // status: 'active',
      });
    }
  }, [rowData]);
  const dispatch = useDispatch();

  const onSubmit = (values) => {
    if (rowData) {
      const payload = { ...values, _id: rowData?._id };
      delete payload.email;
      delete payload.password;
      dispatch(
        updateAdmin({
          data: payload,
          callBack: (err, res) => {
            if (err) return;
            if (res) {
              handleConfirm();
              handleClose();
            }
          },
        }),
      );
    } else {
      dispatch(
        addSubAdminApi({
          data: values,
          callback: (err, res) => {
            if (err) return;
            if (res) {
              handleConfirm();
              handleClose();
            }
          },
        }),
      );
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="lg"
    >
      <Modal.Header className="py-3 text-dark" closeButton>
        <Modal.Title className="fw-bold text-dark">Add Sub Admin</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            onSubmit(values);
          }}
        >
          {({ values, setFieldValue }) => {
            const addPermission = (permission) => {
              if (permission && !values.permissions.includes(permission)) {
                setFieldValue("permissions", [
                  ...values.permissions,
                  permission,
                ]);
              }
            };

            const removePermission = (permission) => {
              if (permission === "profile") return; // Prevent removing default
              setFieldValue(
                "permissions",
                values.permissions.filter((perm) => perm !== permission),
              );
            };

            return (
              <Form>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="name" className="form-label">
                      Name
                    </label>
                    <Field name="name" type="text" className="form-control" />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label htmlFor="email" className="form-label">
                      Email
                    </label>
                    <Field name="email" type="email" className="form-control" />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-danger mt-1"
                    />
                  </div>
                </div>

                {!rowData ? (
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="password" className="form-label">
                        Password
                      </label>
                      <Field
                        name="password"
                        type="password"
                        className="form-control"
                      />
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </div>
                  </div>
                ) : null}

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label htmlFor="permissions" className="form-label">
                      Permissions
                    </label>
                    <Field
                      as="select"
                      name="permission"
                      className="form-select"
                      onChange={(e) => {
                        addPermission(e.target.value);
                        e.target.value = "";
                      }}
                    >
                      <option value="">Select a permission</option>
                      {permissionsList
                        .filter((perm) => perm !== "profile") // ← hide profile option
                        .filter((perm) => !values.permissions.includes(perm))
                        .map((perm) => (
                          <option key={perm} value={perm}>
                            {perm}
                          </option>
                        ))}
                    </Field>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="d-flex flex-wrap gap-2">
                    {values.permissions.map((permission) => (
                      <span
                        key={permission}
                        className="badge bg-primary d-flex align-items-center"
                        style={{ padding: "0.5rem 1rem", fontSize: "0.875rem" }}
                      >
                        {permission}
                        <button
                          type="button"
                          className="btn-close btn-sm ms-2"
                          aria-label="Remove"
                          onClick={() => removePermission(permission)}
                          style={{ fontSize: "0.75rem" }}
                        >
                          <span aria-hidden="true">&times;</span>
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="d-flex justify-content-end">
                  <button type="submit" className="btn btn-primary">
                    Submit
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}
